import React, { useEffect, useState } from 'react';
import { MailPlus, Clock, CheckCircle2, XCircle, Send, Ban, Users } from 'lucide-react';
import invitationsService from '../services/invitations';
import rolesService from '../services/roles';
import { splitRoles } from '../utils/roleFlags';
import { useApps } from '../contexts/AppsContext';
import { resolveDefaultAppId, setSelectedAppId } from '../utils/storage';
import PageHeader from '../components/common/PageHeader';
import SkeletonList from '../components/common/SkeletonList';
import ErrorState from '../components/common/ErrorState';
import Modal from '../components/common/Modal';

const statusConfig = {
    pending: { color: 'text-amber-700 bg-amber-50 border-amber-100', icon: Clock, label: 'Pending' },
    accepted: { color: 'text-emerald-700 bg-emerald-50 border-emerald-100', icon: CheckCircle2, label: 'Accepted' },
    expired: { color: 'text-slate-500 bg-slate-50 border-slate-200', icon: Clock, label: 'Expired' },
    revoked: { color: 'text-red-600 bg-red-50 border-red-100', icon: XCircle, label: 'Revoked' },
};

const InvitationsPage = () => {
    const { apps, loading: loadingApps } = useApps();
    const [appId, setAppId] = useState('');
    const [invitations, setInvitations] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [showSendModal, setShowSendModal] = useState(false);
    const [formData, setFormData] = useState({ email: '', role_ids: [], tenant_id: '' });
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [bulkEmails, setBulkEmails] = useState('');
    const [bulkRoleIds, setBulkRoleIds] = useState([]);
    const [bulkTenantId, setBulkTenantId] = useState('');
    const [bulkSending, setBulkSending] = useState(false);
    const [bulkResults, setBulkResults] = useState(null);

    useEffect(() => {
        if (apps?.length > 0) {
            const defaultAppId = resolveDefaultAppId(apps);
            setAppId(defaultAppId);
            loadInvitations(defaultAppId);
            loadRoles(defaultAppId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apps]);

    const loadInvitations = async (targetAppId) => {
        if (!targetAppId) return;
        setLoading(true);
        setError('');
        try {
            const data = await invitationsService.list(targetAppId);
            setInvitations(data || []);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to load invitations');
        } finally {
            setLoading(false);
        }
    };

    const loadRoles = async (targetAppId) => {
        if (!targetAppId) return;
        try {
            const data = await rolesService.getAll(targetAppId);
            // Flag roles are set on the user form after acceptance, never offered as roles.
            setRoles(splitRoles(data).roles);
        } catch (err) {
            // silent
        }
    };

    const handleAppChange = (e) => {
        const id = e.target.value;
        setAppId(id);
        setSelectedAppId(id);
        if (id) {
            loadInvitations(id);
            loadRoles(id);
        }
    };

    const handleSend = async (e) => {
        e.preventDefault();
        setError('');
        try {
            await invitationsService.send(appId, {
                email: formData.email,
                role_ids: formData.role_ids,
                ...(formData.tenant_id && { tenant_id: formData.tenant_id }),
            });
            setSuccessMessage('Invitation sent!');
            setShowSendModal(false);
            setFormData({ email: '', role_ids: [], tenant_id: '' });
            loadInvitations(appId);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to send invitation');
        }
    };

    const handleRevoke = async (invitationId) => {
        if (!window.confirm('Revoke this invitation?')) return;
        try {
            await invitationsService.revoke(invitationId);
            setSuccessMessage('Invitation revoked.');
            loadInvitations(appId);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to revoke');
        }
    };

    const handleBulkSend = async (e) => {
        e.preventDefault();
        setError('');
        setBulkResults(null);
        setBulkSending(true);

        // Parse emails: split by newlines, commas, or semicolons
        const emails = bulkEmails
            .split(/[\n,;]+/)
            .map((e) => e.trim())
            .filter((e) => e.length > 0);

        if (emails.length === 0) {
            setError('Please enter at least one email address.');
            setBulkSending(false);
            return;
        }

        const invitations = emails.map((email) => ({
            email,
            role_ids: bulkRoleIds,
            ...(bulkTenantId && { tenant_id: bulkTenantId }),
        }));

        try {
            const result = await invitationsService.bulkSend(appId, { invitations });
            setBulkResults(result?.data || result);
            if (result?.data?.succeeded > 0 || result?.succeeded > 0) {
                loadInvitations(appId);
            }
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to send bulk invitations');
        } finally {
            setBulkSending(false);
        }
    };

    const closeBulkModal = () => {
        setShowBulkModal(false);
        setBulkEmails('');
        setBulkRoleIds([]);
        setBulkTenantId('');
        setBulkResults(null);
        setError('');
    };

    return (
        <div className="space-y-4">
            <PageHeader
                title="Invitations"
                subtitle="Invite users to join applications via email"
                actions={
                    <div className="flex gap-2">
                        <select
                            className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 min-w-[200px]"
                            value={appId}
                            onChange={handleAppChange}
                            disabled={loadingApps}
                        >
                            <option value="">Select an App</option>
                            {apps.map((app) => (
                                <option key={app.app_id} value={app.app_id}>
                                    {app.app_name}
                                </option>
                            ))}
                        </select>
                        {appId && (
                            <>
                                <button
                                    onClick={() => {
                                        setFormData({ email: '', role_ids: [], tenant_id: '' });
                                        setShowSendModal(true);
                                    }}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
                                >
                                    <Send className="h-4 w-4" /> Send Invite
                                </button>
                                <button
                                    onClick={() => {
                                        setBulkEmails('');
                                        setBulkRoleIds([]);
                                        setBulkTenantId('');
                                        setBulkResults(null);
                                        setShowBulkModal(true);
                                    }}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-primary-600 text-primary-600 text-sm font-medium hover:bg-primary-50"
                                >
                                    <Users className="h-4 w-4" /> Bulk Invite
                                </button>
                            </>
                        )}
                    </div>
                }
            />

            {successMessage && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 text-sm">
                    {successMessage}
                </div>
            )}

            {loading ? (
                <SkeletonList />
            ) : error ? (
                <ErrorState message={error} />
            ) : !appId ? (
                <div className="rounded-xl border border-slate-200 bg-white/90 p-8 text-center">
                    <p className="text-slate-500">Select an App to view invitations.</p>
                </div>
            ) : invitations.length === 0 ? (
                <div className="rounded-xl border border-slate-200 bg-white/90 p-8 text-center">
                    <MailPlus className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No invitations yet. Send one to get started.</p>
                </div>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {invitations.map((inv) => {
                        const cfg = statusConfig[inv.status] || statusConfig.pending;
                        const StatusIcon = cfg.icon;
                        return (
                            <div
                                key={inv.invitation_id}
                                className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm space-y-3"
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="h-10 w-10 rounded-xl bg-primary-50 text-primary-700 grid place-content-center">
                                            <MailPlus className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <div className="text-sm font-semibold text-slate-900">{inv.email}</div>
                                            <div className="text-xs text-slate-500">
                                                Invited by {inv.invited_by_name || inv.invited_by_username || '—'}
                                            </div>
                                        </div>
                                    </div>
                                    <span
                                        className={`text-xs font-semibold px-2 py-1 rounded-full border flex items-center gap-1 ${cfg.color}`}
                                    >
                                        <StatusIcon className="h-3 w-3" /> {cfg.label}
                                    </span>
                                </div>
                                <div className="text-xs text-slate-500">
                                    Expires: {new Date(inv.token_expiry).toLocaleDateString()}
                                </div>
                                {inv.status === 'pending' && (
                                    <div className="pt-2 border-t border-slate-100">
                                        <button
                                            onClick={() => handleRevoke(inv.invitation_id)}
                                            className="px-3 py-1 rounded-md text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 flex items-center gap-1"
                                        >
                                            <Ban className="h-3 w-3" /> Revoke
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Send Invitation Modal */}
            <Modal isOpen={showSendModal} onClose={() => setShowSendModal(false)} title="Send Invitation" size="md">
                <form onSubmit={handleSend} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                        <input
                            type="email"
                            required
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            placeholder="user@example.com"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Roles *</label>
                        <div className="space-y-2 max-h-32 overflow-y-auto border border-slate-200 rounded-lg p-2">
                            {roles
                                .filter((r) => r.is_active)
                                .map((role) => (
                                    <label key={role.role_id} className="flex items-center gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={formData.role_ids.includes(role.role_id)}
                                            onChange={(e) => {
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    role_ids: e.target.checked
                                                        ? [...prev.role_ids, role.role_id]
                                                        : prev.role_ids.filter((id) => id !== role.role_id),
                                                }));
                                            }}
                                            className="rounded border-slate-300"
                                        />
                                        <span>{role.role_name}</span>
                                    </label>
                                ))}
                        </div>
                    </div>

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3 pt-4 border-t border-slate-200">
                        <button
                            type="submit"
                            disabled={!formData.email || formData.role_ids.length === 0}
                            className="flex-1 rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700 disabled:opacity-50"
                        >
                            Send Invitation
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowSendModal(false)}
                            className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Bulk Invite Modal */}
            <Modal isOpen={showBulkModal} onClose={closeBulkModal} title="Bulk Invite" size="lg">
                <form onSubmit={handleBulkSend} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Email Addresses *{' '}
                            <span className="text-xs text-slate-400 font-normal">
                                (one per line, or comma/semicolon separated, max 100)
                            </span>
                        </label>
                        <textarea
                            required
                            rows={6}
                            value={bulkEmails}
                            onChange={(e) => setBulkEmails(e.target.value)}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 font-mono"
                            placeholder={'user1@example.com\nuser2@example.com\nuser3@example.com'}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Roles *{' '}
                            <span className="text-xs text-slate-400 font-normal">(applied to all invitations)</span>
                        </label>
                        <div className="space-y-2 max-h-32 overflow-y-auto border border-slate-200 rounded-lg p-2">
                            {roles
                                .filter((r) => r.is_active)
                                .map((role) => (
                                    <label key={role.role_id} className="flex items-center gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={bulkRoleIds.includes(role.role_id)}
                                            onChange={(e) => {
                                                setBulkRoleIds((prev) =>
                                                    e.target.checked
                                                        ? [...prev, role.role_id]
                                                        : prev.filter((id) => id !== role.role_id)
                                                );
                                            }}
                                            className="rounded border-slate-300"
                                        />
                                        <span>{role.role_name}</span>
                                    </label>
                                ))}
                        </div>
                    </div>

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    {/* Bulk Results Display */}
                    {bulkResults && (
                        <div className="space-y-2">
                            <div className="flex gap-3">
                                <div className="flex-1 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 text-sm font-medium text-center">
                                    {bulkResults.succeeded} succeeded
                                </div>
                                {bulkResults.failed?.length > 0 && (
                                    <div className="flex-1 rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm font-medium text-center">
                                        {bulkResults.failed.length} failed
                                    </div>
                                )}
                            </div>
                            {bulkResults.failed?.length > 0 && (
                                <div className="rounded-lg border border-red-100 bg-red-50 p-3 max-h-40 overflow-y-auto">
                                    <p className="text-xs font-semibold text-red-700 mb-1">Failed invitations:</p>
                                    {bulkResults.failed.map((f, idx) => (
                                        <div key={idx} className="text-xs text-red-600 py-0.5">
                                            <span className="font-medium">{f.email}</span>: {f.error}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="flex gap-3 pt-4 border-t border-slate-200">
                        {!bulkResults ? (
                            <>
                                <button
                                    type="submit"
                                    disabled={!bulkEmails.trim() || bulkRoleIds.length === 0 || bulkSending}
                                    className="flex-1 rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700 disabled:opacity-50"
                                >
                                    {bulkSending ? 'Sending...' : 'Send All'}
                                </button>
                                <button
                                    type="button"
                                    onClick={closeBulkModal}
                                    className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                                >
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                onClick={closeBulkModal}
                                className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                            >
                                Close
                            </button>
                        )}
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default InvitationsPage;
